/**
 * Logout action — implements the D-07 sequence verbatim.
 *
 * In exact order:
 *   1. POST /api/v1/auth/logout — best-effort, capped at 2s (AUTH-08 +
 *      success criterion 5). Network failure does NOT block local flush.
 *   2. queryClient.removeQueries() — synchronously purge tenant-scoped cache
 *      BEFORE redirect so no cross-tenant data flashes during the logout→
 *      login navigation.
 *   3. BroadcastChannel post `{type: "LOGOUT"}` — notify other tabs (D-08
 *      propagation loop). A tenant sign-out (admin / mobile / accounting)
 *      tells every tenant app's tabs, since the server ends every tenant
 *      session in this browser; a platform sign-out tells only platform tabs.
 *   4. Clear in-memory access token + proactive refresh timer.
 *   5. `window.location.assign(loginPath)` — hard navigation to reset all
 *      in-memory React state.
 *
 * Rationale for API-first ordering: if `/auth/logout` is slow, local flush
 * still runs; if local flush were first, another tab's in-flight query could
 * re-populate the cache before the server revokes the refresh family.
 *
 * QueryClient is imported as type-only so `@linkhr/auth` does NOT pull
 * `@tanstack/react-query` as a runtime dependency. The apps supply the
 * concrete instance at call-time.
 */
import type { QueryClient } from "@tanstack/react-query";
import { getAcceptLanguageHeader } from "../refresh/authNetworkConfig.js";
import { accessTokenStore } from "../tokens/accessTokenStore.js";
import { loggedOutMarker } from "../tokens/loggedOutMarker.js";
import { refreshTokenStore } from "../tokens/refreshTokenStore.js";
import { userEmailHintStore } from "../tokens/userEmailHint.js";
import { clearProactiveRefresh } from "../refresh/proactiveTimer.js";
import { clearSessionSentinel } from "../refresh/sessionSentinel.js";
import { noEmployeeMarker } from "../tokens/noEmployeeMarker.js";
import { sessionTenantMarker } from "../tokens/sessionTenantMarker.js";
import { sessionTenantSlugStore } from "../tokens/sessionTenantSlugStore.js";
import {
  getAudienceHeader,
  getAuthChannel,
  getLogoutPath,
} from "../refresh/crossTabCoordinator.js";
import { authChannelName, getAudience } from "../refresh/authNetworkConfig.js";
import type { AuthBroadcastMessage } from "../channel/broadcastSchema.js";
import type { Audience } from "../types.js";

const TENANT_AUDIENCES: readonly Audience[] = ["admin", "mobile", "accounting"];

export interface PerformLogoutArgs {
  queryClient: QueryClient;
  apiBaseUrl: string;
  /**
   * Defaults to `${import.meta.env.BASE_URL}login` — Vite substitutes BASE_URL
   * at the consumer app's build time, so HR Admin gets `/admin/login`,
   * Employee Web gets `/me/login`, and Link Admin gets `/login`.
   * @default `${import.meta.env.BASE_URL}login`
   */
  loginPath?: string;
}

export async function performLogout({
  queryClient,
  apiBaseUrl,
  loginPath = `${(import.meta as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? "/"}login`,
}: PerformLogoutArgs): Promise<void> {
  // Step 1: best-effort POST /auth/logout (2s cap). Endpoint is audience-aware:
  // platform tokens route through /_platform/auth/logout; tenant tokens use
  // /auth/logout. Calling the wrong one returns 400 and leaves the server-side
  // refresh family un-revoked.
  try {
    await Promise.race([
      fetch(apiBaseUrl + getLogoutPath(), {
        method: "POST",
        credentials: "include",
        // X-Refresh-Audience (CC-277): clear the right per-audience cookie.
        headers: { ...getAudienceHeader(), ...getAcceptLanguageHeader() },
      }),
      new Promise<never>((_, rej) => setTimeout(() => rej(new Error("logout-timeout")), 2000)),
    ]);
  } catch {
    // swallow — D-07 mandates local flush proceeds regardless of network result
  }

  // Step 2: purge tenant-scoped cache BEFORE redirect (AUTH-08).
  // Uses removeQueries (not clear()) per Pitfall C1: clear() races in-flight queries.
  queryClient.removeQueries();

  // Step 3: notify other tabs (D-08). The server ends every tenant session in
  // this browser, so every tenant app's tabs hear it. This app's own channel
  // goes through getAuthChannel(): a second object on the same name would
  // deliver the message back to this tab's listeners.
  const logout = { type: "LOGOUT" } satisfies AuthBroadcastMessage;
  getAuthChannel().postMessage(logout);
  const aud = getAudience();
  if (aud !== "platform") {
    for (const other of TENANT_AUDIENCES) {
      if (other === aud) continue;
      const channel = new BroadcastChannel(authChannelName(other));
      channel.postMessage(logout);
      channel.close();
    }
  }

  // Step 4: clear local state. clearSessionSentinel() expires the non-HttpOnly
  // `linkhr_session` marker ourselves so a best-effort/failed `/auth/logout`
  // (which on staging 400s without clearing it) can't leave a sentinel behind
  // that lets refreshOnLoad silently restore the just-logged-out session.
  accessTokenStore.clear();
  refreshTokenStore.clear();
  userEmailHintStore.clear();
  noEmployeeMarker.clear();
  clearSessionSentinel();
  clearProactiveRefresh();
  // Record the explicit logout so bootstrap doesn't silently restore the
  // session from a surviving HttpOnly cookie (the platform logout endpoint is
  // best-effort and can fail to revoke server-side). Cleared on next login by
  // commitVerifiedSession.
  loggedOutMarker.set();
  // The host's session is over; forget which tenant it belonged to. The slug
  // goes too — on the global entry host it is the ONLY thing naming a company,
  // so leaving it behind would let the next visitor's shell claim this one.
  sessionTenantMarker.clear();
  sessionTenantSlugStore.clear();

  // Step 5: hard nav resets all React state.
  window.location.assign(loginPath);
}
